import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Tabs,
  Form,
  Input,
  Upload,
  Select,
  InputNumber,
  message,
  Result,
  Spin
} from 'antd'
import {
  ShopOutlined,
  ShoppingCartOutlined,
  SettingOutlined,
  RiseOutlined,
  BarChartOutlined,
  UploadOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { getStoreInfo, updateStoreInfo, getStoreStats } from '../api/store'
import { now as tzNow } from '../utils/timezone'

const PageContainer = styled.div`
  padding: 24px;
  background: #f0f2f5;
  min-height: 100vh;
`

const WelcomeCard = styled(Card)`
  margin-bottom: 24px;
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  color: white;

  .ant-card-head-title {
    color: white;
  }
`

const StyledCard = styled(Card)`
  height: 100%;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .ant-card-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .icon-wrapper {
    font-size: 48px;
    margin-bottom: 16px;
    color: #1890ff;
  }
`

const StatisticCard = styled(Card)`
  margin-bottom: 24px;

  .ant-statistic-title {
    font-size: 16px;
  }

  @media (max-width: 768px) {
    .ant-statistic {
      margin-bottom: 20px;
    }
  }
`

const SettingCard = styled(Card)`
  margin-top: 24px;
`

// 添加新的樣式組件
const RestrictedAccess = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
  border-radius: 15px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
`

const RestrictedIcon = styled.div`
  font-size: 80px;
  color: #1890ff;
  margin-bottom: 20px;
  animation: float 3s ease-in-out infinite;

  @keyframes float {
    0% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-10px);
    }
    100% {
      transform: translateY(0px);
    }
  }
`

const RestrictedTitle = styled.h1`
  font-size: 2rem;
  color: #333;
  margin-bottom: 15px;
  font-weight: bold;
`

const RestrictedDescription = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 30px;
  max-width: 600px;
  line-height: 1.6;
`

const UpgradeButton = styled.button`
  padding: 0 30px;
  height: 46px;
  color: white;
  font-size: 1.1rem;
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  border: none;
  border-radius: 25px;
  box-shadow: 0 4px 15px rgba(24, 144, 255, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(213, 114, 138, 0.4);
  }
`

const ShopSetting = () => {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(true)
  const [storeInfo, setStoreInfo] = useState(null)
  const [activating, setActivating] = useState(false)
  const [plan, setPlan] = useState('free') // 這裡應該從你的用戶狀態或API中獲取
  const [stats, setStats] = useState({
    todayOrders: 0,
    monthlyRevenue: 0,
    totalProducts: 0,
    pendingOrders: 0
  })

  // 獲取商店信息
  const fetchStoreInfo = async () => {
    try {
      const res = await getStoreInfo()
      setStoreInfo(res.data.data)
      if (res.data?.data.isEnabled) {
        console.log('res.data', res.data.data)
        form.setFieldsValue({
          shopName: res.data.data.name,
          phone: res.data.data.phone,
          description: res.data.data.description,
          paymentMethods: res.data.data.paymentMethods,
          minimumOrder: res.data.data.minimumOrder,
          shippingMethods: res.data.data.shippingMethods,
          shippingFee: res.data.data.shippingFee,
          freeShippingThreshold: res.data.data.freeShippingThreshold
        })
      }
    } catch (error) {
      // 商店不存在的錯誤處理已在下方的渲染邏輯中處理
    } finally {
      setLoading(false)
    }
  }

  // 獲取統計數據
  const fetchStoreStats = async () => {
    try {
      const res = await getStoreStats()
      if (res.data?.data) {
        setStats(res.data.data)
      }
    } catch (error) {
      if (storeInfo) {
        console.error('獲取統計數據失敗')
      }
    }
  }

  useEffect(() => {
    fetchStoreInfo()
    fetchStoreStats() // 添加獲取統計數據
  }, [form])

  // 處理商店啟用
  const handleActivateStore = async () => {
    setActivating(true)
    try {
      await updateStoreInfo({ isEnabled: true })
      message.success('商店啟用成功')
      // 重新獲取商店信息
      await fetchStoreInfo()
    } catch (error) {
      // 修改這裡，顯示後端返回的錯誤信息
      console.error('商店啟用失敗：', error)
      const errorMessage = error.response?.data?.message || '商店啟用失敗'
      message.error(errorMessage)
    } finally {
      setActivating(false)
    }
  }

  // 處理表單提交
  const handleSubmit = async (values) => {
    try {
      // 將表單數據轉換為 API 所需格式
      const requestData = {
        name: values.shopName,
        description: values.description,
        settings: {
          enableShipping:
            values.shippingMethods?.includes('home_delivery') || false,
          shippingFee: values.shippingFee || 0,
          freeShippingThreshold: values.freeShippingThreshold || 0,
          enablePickup:
            values.shippingMethods?.includes('self_pickup') || false,
          paymentMethods: values.paymentMethods || []
        }
      }

      const response = await updateStoreInfo(requestData)
      message.success('設置保存成功')
      // 重新獲取商店信息
      await fetchStoreInfo()
    } catch (error) {
      console.error('保存設置失敗:', error) // 詳細的錯誤日誌
      const errorMessage = error.response?.data?.message || '設置保存失敗'
      message.error(errorMessage)
    }
  }

  // 更新表單初始值設置
  useEffect(() => {
    if (storeInfo) {
      form.setFieldsValue({
        shopName: storeInfo.name,
        description: storeInfo.description,
        shippingMethods: [
          ...(storeInfo.settings?.enableShipping ? ['home_delivery'] : []),
          ...(storeInfo.settings?.enablePickup ? ['self_pickup'] : [])
        ],
        shippingFee: storeInfo.settings?.shippingFee,
        freeShippingThreshold: storeInfo.settings?.freeShippingThreshold,
        paymentMethods: storeInfo.settings?.paymentMethods || []
      })
    }
  }, [storeInfo, form])

  // 更新支付方式選項
  const paymentOptions = [
    { label: '現金', value: 'cash' },
    // { label: '信用卡', value: 'credit_card' },
    // { label: 'Line Pay', value: 'line_pay' },
    { label: '銀行轉帳', value: 'bank_transfer' }
  ]

  // menuItems 移到組件外部或使用 useMemo
  const menuItems = [
    {
      title: '商品管理',
      icon: <ShopOutlined />,
      path: '/store',
      description: '管理商品、分類和庫存'
    },
    {
      title: '訂單管理',
      icon: <ShoppingCartOutlined />,
      path: '/orders',
      description: '處理訂單、發貨和退款'
    },
    {
      title: '商店設置',
      icon: <SettingOutlined />,
      path: '/settings',
      description: '設置商店信息和支付方式'
    }
  ]

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size='large' />
      </div>
    )
  }

  // 新增會員限制判斷
  if (!storeInfo) {
    return (
      <RestrictedAccess>
        <RestrictedIcon>
          <ShopOutlined />
        </RestrictedIcon>
        <RestrictedTitle>商店管理功能限制</RestrictedTitle>
        <RestrictedDescription>
          親愛的用戶，商店管理功能僅開放給品牌會員及以上等級的用戶使用。
          升級至品牌會員方案，即可享有完整的商店管理功能，包括：
          <br />• 商品管理與庫存追蹤
          <br />• 訂單處理與物流管理
          <br />• 支付方式設置
          <br />• 更多進階功能
        </RestrictedDescription>
        <UpgradeButton
          size='large'
          style={{ marginTop: 10 }}
          loading={activating}
          onClick={handleActivateStore}
        >
          {activating ? '啟用中...' : '已開通，啟用商店'}
        </UpgradeButton>
      </RestrictedAccess>
    )
  }

  return (
    <PageContainer>
      <WelcomeCard>
        <h2 style={{ color: 'white', marginBottom: 0 }}>
          歡迎回來，{storeInfo?.name || ''}
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.8)' }}>
          今天是 {tzNow().format('YYYY/MM/DD')}，祝您生意興隆！
        </p>
      </WelcomeCard>

      <StatisticCard>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={12} md={6}>
            <Statistic
              title='今日訂單'
              value={stats.todayOrders}
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Statistic
              title='本月營業額'
              value={stats.monthlyRevenue}
              prefix='NT$'
              valueStyle={{ color: '#cf1322' }}
            />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Statistic
              title='商品總數'
              value={stats.totalProducts}
              prefix={<BarChartOutlined />}
            />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Statistic
              title='待處理訂單'
              value={stats.pendingOrders}
              valueStyle={{ color: '#faad14' }}
            />
          </Col>
        </Row>
      </StatisticCard>

      <Row gutter={[24, 24]}>
        {menuItems.map((item) => (
          <Col xs={24} sm={12} md={8} key={item.path}>
            <StyledCard onClick={() => navigate(item.path)}>
              <div className='icon-wrapper'>{item.icon}</div>
              <h3>{item.title}</h3>
              <p style={{ color: '#666' }}>{item.description}</p>
              <Button type='primary'>進入管理</Button>
            </StyledCard>
          </Col>
        ))}
      </Row>

      <SettingCard>
        <Tabs defaultActiveKey='1'>
          <Tabs.TabPane tab='基本信息' key='1'>
            <Form layout='vertical' form={form} onFinish={handleSubmit}>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label='商店名稱'
                    name='shopName'
                    rules={[{ required: true, message: '請輸入商店名稱' }]}
                  >
                    <Input placeholder='請輸入商店名稱' />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                label='商店簡介'
                name='description'
                rules={[{ required: true, message: '請輸入商店簡介' }]}
              >
                <Input.TextArea rows={4} placeholder='請輸入商店簡介' />
              </Form.Item>
            </Form>
          </Tabs.TabPane>

          <Tabs.TabPane tab='支付設置' key='2'>
            <Form layout='vertical' form={form}>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label='支付方式'
                    name='paymentMethods'
                    rules={[
                      { required: true, message: '請至少選擇一種支付方式' }
                    ]}
                  >
                    <Select
                      mode='multiple'
                      placeholder='請選擇支付方式'
                      options={paymentOptions}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Tabs.TabPane>

          <Tabs.TabPane tab='物流設置' key='3'>
            <Form layout='vertical' form={form}>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label='配送方式'
                    name='shippingMethods'
                    rules={[
                      { required: true, message: '請至少選擇一種配送方式' }
                    ]}
                  >
                    <Select
                      mode='multiple'
                      placeholder='請選擇配送方式'
                      options={[
                        // { label: '宅配', value: 'home_delivery' },
                        { label: '自取', value: 'self_pickup' }
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label='運費設置'
                    name='shippingFee'
                    rules={[{ required: true, message: '請輸入基本運費' }]}
                  >
                    <InputNumber
                      prefix='NT$'
                      style={{ width: '100%' }}
                      placeholder='請輸入基本運費'
                      min={0}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                label='免運門檻'
                name='freeShippingThreshold'
                rules={[{ required: true, message: '請輸入免運門檻金額' }]}
              >
                <InputNumber
                  prefix='NT$'
                  style={{ width: '100%' }}
                  placeholder='請輸入免運門檻金額'
                  min={0}
                />
              </Form.Item>
            </Form>
          </Tabs.TabPane>
        </Tabs>

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Button
            type='primary'
            size='large'
            onClick={async () => {
              try {
                const values = await form.validateFields()
                console.log('表單驗證通過:', values) // 檢查表單驗證
                await handleSubmit(values)
              } catch (err) {
                console.error('表單驗證失敗:', err)
                if (err.errorFields) {
                  message.error('請檢查表單填寫是否完整')
                }
              }
            }}
          >
            保存設置
          </Button>
        </div>
      </SettingCard>
    </PageContainer>
  )
}

export default ShopSetting
